import { useEffect, useState } from "react";
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css"; // Default styles
import "../App_Tour/shepherd.css";
import { useSharedValue } from "../components/Constants/constants";

const ShepherdTour = () => {
  const [userName, setUserName] = useState("");
  const { handleTourCompleted } = useSharedValue();
  const totalSteps = 11;

  useEffect(() => {
    // Get user data from localStorage
    const userDataJSON = localStorage.getItem("token");
    let userFullName = "";

    if (userDataJSON) {
      const userData = JSON.parse(userDataJSON);
      userFullName = `${userData.firstName} ${userData.lastName}`.trim();
      setUserName(userFullName);
    }
  }, []);

  useEffect(() => {
    // Track the current step index
    let currentStepIndex = 0;

    const tour = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: {
          enabled: false,
        },
        classes: "shepherd-welcome-step",
        buttons: [],
      },
    });

    // Expose tour to window so it can be accessed by onclick handlers
    window.shepherdTour = tour;
    // Also expose the handler for completion
    window.handleTourCompleted = handleTourCompleted;

    // Welcome Step with personalized message, video, and directly embedded buttons
    tour.addStep({
      id: "welcome-user",
      classes: "shepherd-welcome-step",
      text: `
        <div style="text-align: center;">
          <h3 style="margin-top: 15px;">Welcome Aboard!</h3>
          <p>This quick tour will help you understand the key features of Comply Extractor and how to navigate the application.</p>
          
          <!-- Buttons directly in HTML -->
          <div class="custom-buttons-container" style="display: flex; flex-direction: column; align-items: center; margin-top: 20px; gap: 10px;">
            <button id="start-tour-button" style="width: 250px; padding: 8px 16px; background:#2aa7e0; color:white; border:none; border-radius: 4px;">
              Give me a walkthrough
            </button>
            <button id="skip-tour-button" style="width: 250px; padding: 8px 16px; background: #cbebf6; color: black; border: 1px solid #cbebf6; border-radius: 4px; cursor: pointer;">
              Dismiss
            </button>
          </div>
        </div>
      `,
      buttons: [],
      when: {
        show: () => {
          // Add event listeners to the buttons
          setTimeout(() => {
            const startButton = document.getElementById("start-tour-button");
            const skipButton = document.getElementById("skip-tour-button");

            if (startButton) {
              startButton.addEventListener("click", () => {
                // Go to the file-import step explicitly
                tour.show("dashboard");
              });
            }

            if (skipButton) {
              skipButton.addEventListener("click", () => {
                window.handleTourCompleted(true);
                window.shepherdTour.cancel();
              });
            }
          }, 100);
        },
      },
    });

    // Function to create progress dots with completed steps in blue
    const createProgressDots = (stepIndex) => {
      return Array(totalSteps - 1)
        .fill("")
        .map((_, i) => {
          let className = "progress-dot";
          // Current step is active
          if (i === stepIndex) {
            className += " active";
          }
          // Previous steps are completed (blue)
          else if (i < stepIndex) {
            className += " completed";
          }
          return `<span class="${className}"></span>`;
        })
        .join("");
    };

    const createStepContent = (title, description, stepIndex) => {
      const dots = createProgressDots(stepIndex);

      return `
        <div class="step-content">
          <h3 style="text-align: left;">${title}</h3>

          <p style="text-align: left;">${description}</p>
          <div class="step-footer">
            <div class="progress-dots">${dots}</div>
            <div class="step-buttons">
              <button class="shepherd-button-secondary" style="color:#d32f2f; margin-left: 6px;" onclick="window.handleTourCompleted(true); window.shepherdTour.cancel()">Close</button>
              <button class="shepherd-button-secondary" style="margin-right: 2px;" onclick="window.shepherdTour.back()">Back</button>
              <button class="shepherd-button-primary" onclick="window.shepherdTour.next()">Next</button>
            </div>
          </div>
        </div>
      `;
    };

    // Modify the last step to use 'Complete' instead of 'Next'
    const createLastStepContent = (title, description, stepIndex) => {
      const dots = createProgressDots(stepIndex);

      return `
        <div class="step-content">
          <h3 style="text-align: left;">${title}</h3>
          <p style="text-align: left;">${description}</p>
          <div class="step-footer">
            <div class="progress-dots">${dots}</div>
            <div class="step-buttons">
              <button class="shepherd-button-secondary" onclick="window.shepherdTour.back()">Back</button>
              <button class="shepherd-button-primary" onclick="window.handleTourCompleted(true); window.shepherdTour.complete()">Complete</button>
            </div>
          </div>
        </div>
      `;
    };

    // Add steps for each feature
    // Add steps for each feature
    tour.addStep({
      id: "dashboard",
      classes: "shepherd-step",
      text: createStepContent(
        "Dashboard",
        "The 'Dashboard' provides a quick overview of the system status, recent activities, and key metrics related to your document extractions.",
        0
      ),
      attachTo: {
        element: 'div[data-testid="dashboard"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 0;
        },
      },
    });
    tour.addStep({
      id: "file-import",
      classes: "shepherd-step",
      text: createStepContent(
        "File Import",
        "The 'File Import' tab is where the user is able to first upload their files for extraction. In this same tab, the user is required to assign a 'Client' to the file before selecting and starting the extraction process.",
        1
      ),
      attachTo: {
        element: 'div[data-testid="file-import"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 1;
        },
      },
    });

    tour.addStep({
      id: "extraction-review",
      classes: "shepherd-step",
      text: createStepContent(
        "Extraction Review",
        "The 'Extraction Review' tab is where all of the data from the extractor is sent for initial review and data validation. This tab is generally used by the Administrators.",
        2
      ),
      attachTo: {
        element: 'div[data-testid="extraction-review"]',
        on: "left",
      },
      when: {
        show: () => {
          currentStepIndex = 2;
        },
      },
    });

    tour.addStep({
      id: "approval-review",
      classes: "shepherd-step",
      text: createStepContent(
        "Approval Process",
        "The 'Approval Process' tab is generally used by line managers or supervisors who act as Approvers within an organization. The Approver can check all of the extracted data, and validate the documents.",
        3
      ),
      attachTo: {
        element: 'div[data-testid="approval-review"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 3;
        },
      },
    });

    tour.addStep({
      id: "reject",
      classes: "shepherd-step",
      text: createStepContent(
        "Rejections",
        "The 'Rejections' tab is where any rejected extractions will be sent. From here, they can either be removed from the system, or sent back through the extractor for a second pass.",
        4
      ),
      attachTo: {
        element: 'div[data-testid="reject"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 4;
        },
      },
    });

    tour.addStep({
      id: "export",
      classes: "shepherd-step",
      text: createStepContent(
        "Export",
        "The 'Export' tab is where users can download approved extracted data in XLSX or XML format. If you subscribe to Comply Admin, this is where you can send your information directly into your Admin database.",
        5
      ),
      attachTo: {
        element: 'div[data-testid="export"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 5;
        },
      },
    });

    tour.addStep({
      id: "report",
      classes: "shepherd-step",
      text: createStepContent(
        "Reports",
        "The 'Reports' tab is used to extract audit data from the application.",
        6
      ),
      attachTo: {
        element: 'div[data-testid="report"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 6;
        },
      },
    });

    tour.addStep({
      id: "support",
      classes: "shepherd-step",
      text: createStepContent(
        "Help",
        "If you need any further help, or wish to discuss a further license plan with us, feel free to email support@complyexchange.com.",
        7
      ),
      attachTo: {
        element: 'div[data-testid="support"]',
        on: "right",
      },
      when: {
        show: () => {
          currentStepIndex = 7;
        },
      },
    });

    tour.addStep({
      id: "notifications",
      classes: "shepherd-step",
      text: createStepContent("Notifications", "View your notifications here.", 8),
      attachTo: {
        element: 'div[data-testid="notifications"]',
        on: "bottom",
      },
      when: {
        show: () => {
          currentStepIndex = 8;
        },
      },
    });

    tour.addStep({
      id: "copilot",
      classes: "shepherd-step",
      text: createStepContent(
        "Copilot",
        "If you need any further help with navigating the application, feel free to use the integrated and secure copilot feature.",
        10
      ),
      attachTo: {
        element: 'div[data-testid="copilot"]',
        on: "bottom",
      },
      when: {
        show: () => {
          currentStepIndex = 10;
        },
      },
    });

    tour.addStep({
      id: "clients",
      classes: "shepherd-step",
      text: createLastStepContent(
        "Clients",
        "The filter at the top of the screen is used to switch the view of data between different clients. These can be configured against different user profiles in your team to increase security between data sets.",
        11
      ),
      attachTo: {
        element: 'div[data-testid="clients"]',
        on: "bottom",
      },
      when: {
        show: () => {
          currentStepIndex = 11;
        },
      },
    });

    // Event handler for when the welcome step is shown or reshown
    tour.on("show", (event) => {
      if (event.step.id === "welcome-user") {
        // Reattach event listeners to the buttons in case the DOM was updated
        setTimeout(() => {
          const startButton = document.getElementById("start-tour-button");
          const skipButton = document.getElementById("skip-tour-button");

          if (startButton) {
            // Remove existing listeners to prevent duplicates
            startButton.replaceWith(startButton.cloneNode(true));
            const newStartButton = document.getElementById("start-tour-button");
            newStartButton.addEventListener("click", () => {
              // Use tour.show with the specific ID to ensure we go to the file-import step
              tour.show("file-import");
            });
          }

          if (skipButton) {
            // Remove existing listeners to prevent duplicates
            skipButton.replaceWith(skipButton.cloneNode(true));
            const newSkipButton = document.getElementById("skip-tour-button");
            newSkipButton.addEventListener("click", () => {
              window.handleTourCompleted(true);
              window.shepherdTour.cancel();
            });
          }
        }, 100);
      }
    });

    // Start the tour
    tour.start();

    return () => {
      // Clean up global variables on unmount
      delete window.shepherdTour;
      delete window.handleTourCompleted;
      tour.complete();
    };
  }, [userName, handleTourCompleted]);

  return null;
};

export default ShepherdTour;
